import { mapCurrencyFromDTO } from '@/modules/common/mappers/currency.mapper';
import { mapAttributeValueFromResponse } from '@/modules/common/mappers/custom-attribute.mapper';
import { mapRateSourceFromDTO } from '@/modules/common/mappers/rate-source.mapper';
import { RateSourceDTO } from '@/modules/common/types/dto/rate-source.dto';
import { ProjectTeamMemberType } from '@/modules/projects';

import {
  PatchEntityEffectActionDTO,
  PatchEntityEffectDTO,
  ProjectBillingListResponseDTO,
  ProjectBillingResponseDTO,
  ProjectBillingSnapshotResponseDTO,
  ProjectInfoListResponseDTO,
  ProjectInfoSnapshotResponseDTO,
  ProjectInfoVersionedFieldsResponseDTO,
} from '../types/dto/project-conflict.dto';
import {
  ConflictResolutionData,
  PatchEffectAction,
  PatchEntityEffect,
  ProjectBillingSnapshot,
  ProjectBillingSnapshotData,
  ProjectInfoSnapshot,
  ProjectInfoVersionedFields,
} from '../types/project-conflicts';
import { mapProjectBillingModelFromDTO } from './project-billing-model.mapper';
import { mapProjectStatusFromDTO } from './project-status.mapper';

/**
 * Maps patch effect action from DTO to domain enum
 */
export const mapPatchEffectActionFromDTO = (dto: PatchEntityEffectActionDTO): PatchEffectAction => {
  switch (dto) {
    case PatchEntityEffectActionDTO.DELETE:
      return PatchEffectAction.Delete;
    case PatchEntityEffectActionDTO.CUT:
      return PatchEffectAction.Cut;
    default:
      return PatchEffectAction.Cut;
  }
};

/**
 * Maps patch effect action to DTO for API requests
 */
export const mapPatchEffectActionToDTO = (
  action: PatchEffectAction
): PatchEntityEffectActionDTO => {
  switch (action) {
    case PatchEffectAction.Delete:
      return PatchEntityEffectActionDTO.DELETE;
    case PatchEffectAction.Cut:
      return PatchEntityEffectActionDTO.CUT;
    default:
      return PatchEntityEffectActionDTO.CUT;
  }
};

/**
 * Maps patch entity effect from DTO to domain model
 */
export const mapPatchEntityEffectFromDTO = (dto: PatchEntityEffectDTO): PatchEntityEffect => {
  return {
    action: mapPatchEffectActionFromDTO(dto.action),
    cutEffectiveStartDate: dto.cutEffectiveStartDate,
    cutEffectiveEndDate: dto.cutEffectiveEndDate,
  };
};

/**
 * Maps project info versioned fields from DTO to domain model
 */
export const mapProjectInfoVersionedFieldsFromDTO = (
  dto: ProjectInfoVersionedFieldsResponseDTO
): ProjectInfoVersionedFields => {
  return {
    status: mapProjectStatusFromDTO(dto.status),
    tentativeProbability:
      dto.tentativeProbability !== null && dto.tentativeProbability !== undefined
        ? dto.tentativeProbability * 100
        : dto.tentativeProbability,
    managers: (dto.managers ?? []).map(m => ({
      id: m.id,
      email: m.email,
      name: m.name,
      profileImageUrl: m.profileImageUrl,
      isDeleted: m.isDeleted,
    })),
    projectStartDate: dto.projectStartDate,
    projectEndDate: dto.projectEndDate,
    industries: dto.industries,
    customAttributesValues: dto.customAttributesValues.map(mapAttributeValueFromResponse),
  };
};

/**
 * Maps project info snapshot from DTO to domain model
 */
export const mapProjectInfoSnapshotFromDTO = (
  dto: ProjectInfoSnapshotResponseDTO
): ProjectInfoSnapshot => {
  return {
    snapshot: mapProjectInfoVersionedFieldsFromDTO(dto.snapshot),
    patchEffect: mapPatchEntityEffectFromDTO(dto.patchEffect),
    effectiveStartDate: dto.effectiveStartDate,
    effectiveEndDate: dto.effectiveEndDate,
  };
};

/**
 * Maps project info conflicts list from DTO to domain model
 */
export const mapProjectInfoConflictsFromDTO = (
  dto: ProjectInfoListResponseDTO
): ConflictResolutionData => {
  return {
    items: dto.items.map(mapProjectInfoSnapshotFromDTO),
  };
};

/**
 * Maps project billing snapshot data from DTO to domain model
 */
export const mapProjectBillingSnapshotDataFromDTO = (
  dto: ProjectBillingResponseDTO
): ProjectBillingSnapshotData => {
  return {
    billingModel: dto.billingModel ? mapProjectBillingModelFromDTO(dto.billingModel) : null,
    defaultCurrency: mapCurrencyFromDTO(dto.defaultCurrency),
    rateSource: mapRateSourceFromDTO(dto.rateSource),
    flatRate: dto.flatRate,
    budget: dto.budget,
    userRates: dto.userRates.map(userRate => {
      const activeRateSource = mapRateSourceFromDTO(userRate.rateSource);

      return {
        id: String(userRate.user.id),
        name: userRate.user.name,
        email: userRate.user.email ?? undefined,
        avatarLink: userRate.user.profileImageUrl || undefined,
        type: userRate.hasAssignment
          ? ProjectTeamMemberType.HasAssignments
          : ProjectTeamMemberType.ManuallyAdded,
        activeRateSource,
        userExternalRateValue:
          userRate.rateOptions.find(opt => opt.source === RateSourceDTO.USER)?.rate ?? 0,
        seniorityBasedRateValue:
          userRate.rateOptions.find(opt => opt.source === RateSourceDTO.SENIORITY)?.rate ?? 0,
        roleBasedRateValue:
          userRate.rateOptions.find(opt => opt.source === RateSourceDTO.ROLE)?.rate ?? 0,
        globalDefaultRateValue:
          userRate.rateOptions.find(opt => opt.source === RateSourceDTO.ORGANIZATION)?.rate ?? 0,
        projectRateCardValue:
          userRate.rateOptions.find(opt => opt.source === RateSourceDTO.RATE_CARD)?.rate ?? 0,
        customRateValue:
          activeRateSource === mapRateSourceFromDTO(RateSourceDTO.CUSTOM) ||
          activeRateSource === mapRateSourceFromDTO(RateSourceDTO.FLAT)
            ? userRate.rate
            : undefined,
      };
    }),
    hasAnyDeletedRelations: dto.hasAnyDeletedRelations,
  };
};

/**
 * Maps project billing snapshot from DTO to domain model
 */
export const mapProjectBillingSnapshotFromDTO = (
  dto: ProjectBillingSnapshotResponseDTO
): ProjectBillingSnapshot => {
  return {
    snapshot: mapProjectBillingSnapshotDataFromDTO(dto.snapshot),
    patchEffect: mapPatchEntityEffectFromDTO(dto.patchEffect),
    effectiveStartDate: dto.effectiveStartDate,
    effectiveEndDate: dto.effectiveEndDate,
  };
};

/**
 * Maps project billing conflicts list from DTO to domain model
 */
export const mapProjectBillingConflictsFromDTO = (
  dto: ProjectBillingListResponseDTO
): ConflictResolutionData => {
  return {
    items: dto.items.map(mapProjectBillingSnapshotFromDTO),
  };
};
